import * as math from 'mathjs';
import Fkin from "./Fkin";

/**
 * RT_R6_q4_q5_q6 - Calculation of q4, q5, q6 for R6 robot
 * W. Weber, Maerz/April 2023
 * 
 * @param {Array|Matrix} q_akt - Current joint angles [q1, q2, q3, 0, 0, 0]
 * @param {Matrix} A60 - Rotation matrix (normalized)
 * @param {Array|Matrix} p_06 - Position vector
 * @param {Object} r - Robot object
 * @param {number} eps_q5 - Threshold for q5 singularity
 * @param {number} eps_q5_krit - Critical threshold for q5 singularity
 * @param {number} phi1 - Rotation angle for modification near singularity
 * @param {Array|Matrix} q_alt - Previous solution
 * @returns {Array} [q4_1, q4_2, q5_1, q5_2, q6_1, q6_2] - Two solution sets
 */
class RT_R6_q4_q5_q6 {
  run(q_akt, A60, p_06, r, eps_q5, eps_q5_krit, phi1, q_alt) {
    let fkin = new Fkin();
    
    let d = r.joints.map(j => j.d);
    let d4 = d[4];
    let d6 = d[6];
    
    // Berechnung von A30 und p03, A36, p36 in K0
    let [T, T0] = fkin.run(r, q_akt); // da q_akt=[q1, q2, q3, 0, 0, 0] ist, kann A30, A63, p03, p36 berechnet werden
    
    // Check T0 dimensions - MATLAB T0(1:3,1:3,3) means 3rd frame (1-indexed) = index 2 (0-indexed)
    // But we need to ensure T0 is 3D and has at least 3 frames
    let T0_size = T0.size();
    let frame_idx = 2; // 3rd frame (0-based index for 3rd frame)
    let A30;
    // If T0 is 2D (4x4), use index 0. If 3D, use frame_idx but ensure it's within bounds
    if (T0_size.length === 2) {
      // T0 is 2D (4x4), use the whole matrix
      frame_idx = 0;
      A30 = math.subset(T0, math.index([0, 1, 2], [0, 1, 2]));
    } else if (T0_size.length === 3 && T0_size[2] > frame_idx) {
      // T0 is 3D and has enough frames
      A30 = math.squeeze(math.subset(T0, math.index([0, 1, 2], [0, 1, 2], frame_idx)));
    } else {
      // Fallback: use the last available frame
      frame_idx = T0_size.length === 3 ? T0_size[2] - 1 : 0;
      A30 = math.squeeze(math.subset(T0, math.index([0, 1, 2], [0, 1, 2], frame_idx)));
    }
    let x3 = math.squeeze(math.subset(A30, math.index([0, 1, 2], 0)));
    x3 = math.divide(x3, math.norm(x3));
    let y3 = math.squeeze(math.subset(A30, math.index([0, 1, 2], 1)));
    y3 = math.divide(y3, math.norm(y3));
    let z3 = math.squeeze(math.subset(A30, math.index([0, 1, 2], 2)));
    z3 = math.divide(z3, math.norm(z3));
    
    // A30 neu definiert wegen vorhergehender Normierung der Spaltenvektoren
    let x3_arr = math.squeeze(x3).toArray ? math.squeeze(x3).toArray() : (Array.isArray(math.squeeze(x3)) ? math.squeeze(x3) : [math.squeeze(x3)]);
    let y3_arr = math.squeeze(y3).toArray ? math.squeeze(y3).toArray() : (Array.isArray(math.squeeze(y3)) ? math.squeeze(y3) : [math.squeeze(y3)]);
    let z3_arr = math.squeeze(z3).toArray ? math.squeeze(z3).toArray() : (Array.isArray(math.squeeze(z3)) ? math.squeeze(z3) : [math.squeeze(z3)]);
    let A30_data = [
      [x3_arr[0], y3_arr[0], z3_arr[0]],
      [x3_arr[1], y3_arr[1], z3_arr[1]],
      [x3_arr[2], y3_arr[2], z3_arr[2]]
    ];
    A30 = math.matrix(A30_data);
    
    let ntcp = math.subset(A60, math.index([0, 1, 2], 2)); // A60[:,3] -> column 2 (0-based)
    
    // A63=A30'*A60; % A60=A30*A63 ->A63=A30'*A60
    let A63 = math.multiply(math.transpose(A30), A60);
    let x63 = math.squeeze(math.subset(A63, math.index([0, 1, 2], 0)));
    x63 = math.divide(x63, math.norm(x63));
    let y63 = math.squeeze(math.subset(A63, math.index([0, 1, 2], 1)));
    y63 = math.divide(y63, math.norm(y63));
    let z63 = math.squeeze(math.subset(A63, math.index([0, 1, 2], 2)));
    z63 = math.divide(z63, math.norm(z63));
    
    // Berechnung |q5| und q5_1
    let z3_ntcp = math.multiply(math.transpose(z3), ntcp);
    let z3_ntcp_val = math.squeeze(z3_ntcp);
    z3_ntcp_val = typeof z3_ntcp_val === 'number' ? z3_ntcp_val : (z3_ntcp_val && z3_ntcp_val.valueOf ? z3_ntcp_val.valueOf() : parseFloat(z3_ntcp_val));
    let q5_betrag = Math.abs(Math.acos(z3_ntcp_val));
    
    // Ermittlung Vorzeichen von q5, da über den acos das Vorzeichen von q5 nicht eindeutig ist
    // Extract scalar values from z63 using subset to avoid dimension mismatch
    // z63 is a column vector (3x1) or row vector (1x3) after normalization
    let z63_squeezed = math.squeeze(z63);
    let z63_arr = z63_squeezed.toArray ? z63_squeezed.toArray() : (Array.isArray(z63_squeezed) ? z63_squeezed : [z63_squeezed]);
    // Handle both row and column vectors - MATLAB uses 1-based indexing, JS uses 0-based
    // z63(1) in MATLAB = z63[0] in JS, z63(2) in MATLAB = z63[1] in JS
    let z63_1_val = z63_arr.length > 0 ? (typeof z63_arr[0] === 'number' ? z63_arr[0] : (z63_arr[0] && z63_arr[0].valueOf ? z63_arr[0].valueOf() : parseFloat(z63_arr[0]))) : 0;
    let z63_2_val = z63_arr.length > 1 ? (typeof z63_arr[1] === 'number' ? z63_arr[1] : (z63_arr[1] && z63_arr[1].valueOf ? z63_arr[1].valueOf() : parseFloat(z63_arr[1]))) : 0;
    let q4_start = Math.atan2(z63_2_val, z63_1_val); // q4 theoretisch, diese Rechnung kann in der sing. Stellung kritisch werden
    
    // Vorzeichen von q5_1
    let Vorz;
    let q4_start_val = q4_start;
    if (z63_1_val >= z63_2_val) {
      let cos_q4 = Math.cos(q4_start_val);
      Vorz = cos_q4 !== 0 ? Math.sign(z63_1_val / cos_q4) : 1;
    } else {
      let sin_q4 = Math.sin(q4_start_val);
      Vorz = sin_q4 !== 0 ? Math.sign(z63_2_val / sin_q4) : 1;
    }
    
    let q5_1 = Vorz * q5_betrag;
    
    // Modifikation von A63 in der Nähe singulärer Stellungen
    // wenn in der Nähe sing. Stellung wird A63 etwas gedreht!
    // Die Drehung erfolgt um den (kleinen) Winkel phi1 um die y6-Achse mit dem Betrag phi1
    let ASt6 = math.matrix([[Math.cos(phi1), 0, Math.sin(phi1)],
                            [0, 1, 0],
                            [-Math.sin(phi1), 0, Math.cos(phi1)]]);
    
    // A63 modifiziert
    let A63_mod = math.multiply(A63, ASt6);
    let x63_mod = math.squeeze(math.subset(A63_mod, math.index([0, 1, 2], 0)));
    x63_mod = math.divide(x63_mod, math.norm(x63_mod));
    let y63_mod = math.squeeze(math.subset(A63_mod, math.index([0, 1, 2], 1)));
    y63_mod = math.divide(y63_mod, math.norm(y63_mod));
    let z63_mod = math.squeeze(math.subset(A63_mod, math.index([0, 1, 2], 2)));
    z63_mod = math.divide(z63_mod, math.norm(z63_mod));
    let x63_mod_arr = math.squeeze(x63_mod).toArray ? math.squeeze(x63_mod).toArray() : (Array.isArray(math.squeeze(x63_mod)) ? math.squeeze(x63_mod) : [math.squeeze(x63_mod)]);
    let y63_mod_arr = math.squeeze(y63_mod).toArray ? math.squeeze(y63_mod).toArray() : (Array.isArray(math.squeeze(y63_mod)) ? math.squeeze(y63_mod) : [math.squeeze(y63_mod)]);
    let z63_mod_arr = math.squeeze(z63_mod).toArray ? math.squeeze(z63_mod).toArray() : (Array.isArray(math.squeeze(z63_mod)) ? math.squeeze(z63_mod) : [math.squeeze(z63_mod)]);
    let A63_mod_data = [
      [x63_mod_arr[0], y63_mod_arr[0], z63_mod_arr[0]],
      [x63_mod_arr[1], y63_mod_arr[1], z63_mod_arr[1]],
      [x63_mod_arr[2], y63_mod_arr[2], z63_mod_arr[2]]
    ];
    A63_mod = math.matrix(A63_mod_data);
    
    let q4_1, q4_2, q5_2;
    
    if (q5_betrag >= eps_q5_krit) {
      // hier sing. Stellung q5=0 unkritisch
      q4_1 = Math.atan2(z63_2_val, z63_1_val);
      
      // 2. Lösungen:
      // Die Drehung um pi bei q4a wird vorzeichenabhängig von der Loesung q4a vorgenommen
      let q4_1_val = q4_1;
      q4_2 = q4_1_val - Math.sign(q4_1_val) * Math.PI;
      let q5_1_val = typeof q5_1 === 'number' ? q5_1 : (q5_1 && q5_1.valueOf ? q5_1.valueOf() : parseFloat(q5_1));
      q5_2 = -q5_1_val;
    } else if (q5_betrag > eps_q5) {
      // Vorsicht: Nähe zu einer singulaeren Stellung (q5=0)
      // Extract scalar values from z63_mod using subset to avoid dimension mismatch
      let z63_mod_squeezed = math.squeeze(z63_mod);
      let z63_mod_arr = z63_mod_squeezed.toArray ? z63_mod_squeezed.toArray() : (Array.isArray(z63_mod_squeezed) ? z63_mod_squeezed : [z63_mod_squeezed]);
      let z63_mod_1_val = z63_mod_arr.length > 0 ? (typeof z63_mod_arr[0] === 'number' ? z63_mod_arr[0] : (z63_mod_arr[0] && z63_mod_arr[0].valueOf ? z63_mod_arr[0].valueOf() : parseFloat(z63_mod_arr[0]))) : 0;
      let z63_mod_2_val = z63_mod_arr.length > 1 ? (typeof z63_mod_arr[1] === 'number' ? z63_mod_arr[1] : (z63_mod_arr[1] && z63_mod_arr[1].valueOf ? z63_mod_arr[1].valueOf() : parseFloat(z63_mod_arr[1]))) : 0;
      q4_1 = Math.atan2(z63_mod_2_val, z63_mod_1_val); // hier wird mit modifiziertem A63 gerechnet
      
      let q4_1_val = q4_1;
      q4_2 = q4_1_val - Math.sign(q4_1_val) * Math.PI;
      let q5_1_val = typeof q5_1 === 'number' ? q5_1 : (q5_1 && q5_1.valueOf ? q5_1.valueOf() : parseFloat(q5_1));
      q5_2 = -q5_1_val;
    } else {
      // hier q5_betrag <= eps_q5 (singuläre Stellung --> q5=0)
      // Vorsicht singulaere Stellung (q5=0)
      // A63[2,1] -> row 1, col 0 (0-based), A63[1,1] -> row 0, col 0 (0-based)
      let A63_21 = math.subset(A63, math.index(1, 0));
      A63_21 = typeof A63_21 === 'number' ? A63_21 : (A63_21 && A63_21.valueOf ? A63_21.valueOf() : parseFloat(A63_21));
      let A63_11 = math.subset(A63, math.index(0, 0));
      A63_11 = typeof A63_11 === 'number' ? A63_11 : (A63_11 && A63_11.valueOf ? A63_11.valueOf() : parseFloat(A63_11));
      let sq = Math.atan2(A63_21, A63_11); // sq=q4+q6
      // q_alt[4] -> index 4 (0-based), q_alt[6] -> index 6 (0-based)
      let q_alt_arr = math.squeeze(q_alt).toArray ? math.squeeze(q_alt).toArray() : (Array.isArray(math.squeeze(q_alt)) ? math.squeeze(q_alt) : [math.squeeze(q_alt)]);
      let q_alt_4 = q_alt_arr.length > 4 ? (typeof q_alt_arr[4] === 'number' ? q_alt_arr[4] : (q_alt_arr[4] && q_alt_arr[4].valueOf ? q_alt_arr[4].valueOf() : parseFloat(q_alt_arr[4]))) : 0;
      let q_alt_6 = q_alt_arr.length > 6 ? (typeof q_alt_arr[6] === 'number' ? q_alt_arr[6] : (q_alt_arr[6] && q_alt_arr[6].valueOf ? q_alt_arr[6].valueOf() : parseFloat(q_alt_arr[6]))) : 0;
      q4_1 = 0.5 * (q_alt_4 - q_alt_6 + sq); // minimaler Abstand zur alten lösung
      q4_2 = q4_1;
      q5_1 = 0;
      q5_2 = 0;
    }
    
    // +++++++++++++++++++++++++++++++++++++++++++++++++
    // Berechnung der zwei Lösungen für q6
    // 1. Lösung
    // q_akt(1,1) in MATLAB -> index 0 (0-based), q_akt(2,1) -> index 1, q_akt(3,1) -> index 2
    let q_akt_arr = math.squeeze(q_akt).toArray ? math.squeeze(q_akt).toArray() : (Array.isArray(math.squeeze(q_akt)) ? math.squeeze(q_akt) : [math.squeeze(q_akt)]);
    let q_akt_1 = [q_akt_arr[0], q_akt_arr[1], q_akt_arr[2], q4_1, q5_1, 0];
    let [T1, T0_1] = fkin.run(r, q_akt_1);
    // T0_1[1:3,1:3,5] -> index 5 for 6th frame (0-based), but check dimensions first
    let T0_1_size = T0_1.size();
    let frame_idx_1 = 5; // 6th frame (0-based)
    let A50;
    if (T0_1_size.length === 2) {
      A50 = math.subset(T0_1, math.index([0, 1, 2], [0, 1, 2]));
    } else if (T0_1_size.length === 3 && T0_1_size[2] > frame_idx_1) {
      A50 = math.squeeze(math.subset(T0_1, math.index([0, 1, 2], [0, 1, 2], frame_idx_1)));
    } else {
      frame_idx_1 = T0_1_size.length === 3 ? T0_1_size[2] - 1 : 0;
      A50 = math.squeeze(math.subset(T0_1, math.index([0, 1, 2], [0, 1, 2], frame_idx_1)));
    }
    let A65 = math.multiply(math.transpose(A50), A60); // A60 oben definiert (vorgegebene Orientierung
    let x65 = math.squeeze(math.subset(A65, math.index([0, 1, 2], 0)));
    x65 = math.divide(x65, math.norm(x65));
    let y65 = math.squeeze(math.subset(A65, math.index([0, 1, 2], 1)));
    y65 = math.divide(y65, math.norm(y65));
    
    // Extract scalar values from x65 for q6_1 using subset to avoid dimension mismatch
    let x65_squeezed = math.squeeze(x65);
    let x65_arr = x65_squeezed.toArray ? x65_squeezed.toArray() : (Array.isArray(x65_squeezed) ? x65_squeezed : [x65_squeezed]);
    let x65_1_val = x65_arr.length > 0 ? (typeof x65_arr[0] === 'number' ? x65_arr[0] : (x65_arr[0] && x65_arr[0].valueOf ? x65_arr[0].valueOf() : parseFloat(x65_arr[0]))) : 0;
    let x65_2_val = x65_arr.length > 1 ? (typeof x65_arr[1] === 'number' ? x65_arr[1] : (x65_arr[1] && x65_arr[1].valueOf ? x65_arr[1].valueOf() : parseFloat(x65_arr[1]))) : 0;
    let q6_1 = Math.atan2(x65_2_val, x65_1_val);
    
    // 2. Loesung
    let q_akt_2 = [q_akt_arr[0], q_akt_arr[1], q_akt_arr[2], q4_2, q5_2, 0];
    let [T2, T0_2] = fkin.run(r, q_akt_2);
    // T0_2[1:3,1:3,5] -> index 5 for 6th frame (0-based), but check dimensions first
    let T0_2_size = T0_2.size();
    let frame_idx_2 = 5; // 6th frame (0-based)
    if (T0_2_size.length === 2) {
      A50 = math.subset(T0_2, math.index([0, 1, 2], [0, 1, 2]));
    } else if (T0_2_size.length === 3 && T0_2_size[2] > frame_idx_2) {
      A50 = math.squeeze(math.subset(T0_2, math.index([0, 1, 2], [0, 1, 2], frame_idx_2)));
    } else {
      frame_idx_2 = T0_2_size.length === 3 ? T0_2_size[2] - 1 : 0;
      A50 = math.squeeze(math.subset(T0_2, math.index([0, 1, 2], [0, 1, 2], frame_idx_2)));
    }
    A65 = math.multiply(math.transpose(A50), A60); // A60 oben definiert (vorgegebene Orientierung
    x65 = math.squeeze(math.subset(A65, math.index([0, 1, 2], 0)));
    x65 = math.divide(x65, math.norm(x65));
    y65 = math.squeeze(math.subset(A65, math.index([0, 1, 2], 1)));
    y65 = math.divide(y65, math.norm(y65));
    // Extract scalar values from x65 for q6_2 using subset to avoid dimension mismatch
    x65_squeezed = math.squeeze(x65);
    x65_arr = x65_squeezed.toArray ? x65_squeezed.toArray() : (Array.isArray(x65_squeezed) ? x65_squeezed : [x65_squeezed]);
    x65_1_val = x65_arr.length > 0 ? (typeof x65_arr[0] === 'number' ? x65_arr[0] : (x65_arr[0] && x65_arr[0].valueOf ? x65_arr[0].valueOf() : parseFloat(x65_arr[0]))) : 0;
    x65_2_val = x65_arr.length > 1 ? (typeof x65_arr[1] === 'number' ? x65_arr[1] : (x65_arr[1] && x65_arr[1].valueOf ? x65_arr[1].valueOf() : parseFloat(x65_arr[1]))) : 0;
    let q6_2 = Math.atan2(x65_2_val, x65_1_val);
    
    return [q4_1, q4_2, q5_1, q5_2, q6_1, q6_2];
  }
}

export default RT_R6_q4_q5_q6;

